"use strict";
/**
 * Core HermisError class and type guard
 *
 * Provides type-safe error creation and handling with rich context
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.HermisError = void 0;
exports.isHermisError = isHermisError;
exports.createHermisError = createHermisError;
exports.wrapError = wrapError;
var message_formatter_1 = require("./message-formatter");
/**
 * Base class for all Hermis SDK errors
 *
 * Features:
 * - Type-safe error codes
 * - Rich context data
 * - Automatic message formatting
 * - Stack trace preservation
 * - Original error chaining
 */
var HermisError = /** @class */ (function (_super) {
    __extends(HermisError, _super);
    /**
     * Create a new HermisError
     *
     * @param code - The error code
     * @param context - Context data for the error
     * @param cause - Optional original error that caused this error
     */
    function HermisError(code, context, cause) {
        var _this = this;
        // Format the error message
        var message = (0, message_formatter_1.formatErrorMessage)(code, context);
        // Call Error constructor
        _this = _super.call(this, message) || this;
        // Set error properties
        _this.name = 'HermisError';
        _this.code = code;
        _this.context = context;
        _this.cause = cause;
        // Maintain proper stack trace (only available in V8 engines like Chrome and Node)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(_this, HermisError);
        }
        // If there's a cause, append its stack trace
        if (cause === null || cause === void 0 ? void 0 : cause.stack) {
            _this.stack = "".concat(_this.stack, "\nCaused by: ").concat(cause.stack);
        }
        return _this;
    }
    /**
     * Get a detailed string representation of this error
     * Includes error code, message, and context
     */
    HermisError.prototype.toString = function () {
        var contextStr = (0, message_formatter_1.formatContext)(this.context);
        var parts = [
            "".concat(this.name, " [").concat(this.code, "]: ").concat(this.message),
            contextStr ? "Context: ".concat(contextStr) : null,
            this.cause ? "Caused by: ".concat(this.cause.message) : null,
        ].filter(Boolean);
        return parts.join('\n');
    };
    /**
     * Convert error to a JSON-serializable object
     * Useful for logging and error reporting
     */
    HermisError.prototype.toJSON = function () {
        var _a;
        return {
            name: this.name,
            code: this.code,
            message: this.message,
            context: this.context,
            cause: (_a = this.cause) === null || _a === void 0 ? void 0 : _a.message,
            stack: this.stack,
        };
    };
    return HermisError;
}(Error));
exports.HermisError = HermisError;
/**
 * Type guard to check if an error is a HermisError
 *
 * Usage:
 * ```ts
 * try {
 *   await wallet.connect();
 * } catch (error) {
 *   if (isHermisError(error, HERMIS_ERROR__WALLET_CONNECTION__FAILED)) {
 *     // TypeScript knows error.context has walletName, reason, etc.
 *     console.log(`Failed to connect to ${error.context.walletName}`);
 *   }
 * }
 * ```
 *
 * @param error - The error to check
 * @param code - Optional specific error code to check for
 * @returns True if error is a HermisError (with specific code if provided)
 */
function isHermisError(error, code) {
    if (!(error instanceof HermisError)) {
        return false;
    }
    // If a specific code is provided, check for exact match
    if (code !== undefined) {
        return error.code === code;
    }
    return true;
}
/**
 * Helper to create a HermisError instance
 *
 * Provides better type inference than using `new HermisError()` directly
 *
 * @param code - The error code
 * @param context - Context data for the error
 * @param cause - Optional original error that caused this error
 * @returns A new HermisError instance
 */
function createHermisError(code, context, cause) {
    return new HermisError(code, context, cause);
}
/**
 * Wrap an unknown error in a HermisError
 *
 * Useful for standardizing error handling in catch blocks
 *
 * @param error - The error to wrap
 * @param code - The error code to use for the wrapper
 * @param context - Context data for the wrapper error
 * @returns A HermisError instance
 */
function wrapError(error, code, context) {
    var cause = error instanceof Error ? error : new Error(String(error));
    return new HermisError(code, context, cause);
}
